import React, { useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useBrandData } from '../../hooks/useBrandData';

const isLight = (hex) => {
  const v = hex.replace('#', '');
  const n = parseInt(v.length === 3 ? v.split('').map((c) => c + c).join('') : v, 16);
  const r = (n >> 16) & 0xff;
  const g = (n >> 8) & 0xff;
  const b = n & 0xff;
  // Relative luminance approximation
  return (0.299 * r + 0.587 * g + 0.114 * b) > 200;
};

const ColorSwatch = ({ name, hex, rgb, description }) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const swatchStyle = {
    background: hex,
    border: isLight(hex) ? '1px solid #E6E0DB' : 'none',
  };

  return (
    <Card className="overflow-hidden" data-testid={`color-${hex.replace('#', '').toLowerCase()}`}>
      <div className="h-32" style={swatchStyle} />
      <div className="p-4">
        <h4 className="font-display font-normal text-body mb-2 text-center uppercase tracking-wide">{name}</h4>
        <p className="text-xs text-subtle mb-3 text-center">{description}</p>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-mono text-subtle">HEX: {hex}</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => copyToClipboard(hex, 'HEX')}
              data-testid={`copy-hex-${hex.replace('#', '').toLowerCase()}`}
            >
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
            </Button>
          </div>
          <div className="text-xs font-mono text-subtle">RGB: {rgb}</div>
        </div>
      </div>
    </Card>
  );
};

const ColorSection = ({ title, colors, gridCols }) => (
  <div>
    <h2 className="text-2xl font-heading font-semibold text-jackstone-blue mb-4">{title}</h2>
    <div className={`grid gap-4 ${gridCols}`}>
      {colors.map((c) => (
        <ColorSwatch key={`${title}-${c.name}-${c.hex}`} {...c} />
      ))}
    </div>
  </div>
);

export default function ColorPalette() {
  const { data, loading, error } = useBrandData();

  if (loading) {
    return <div className="text-subtle" data-testid="colors-loading">Loading brand data…</div>;
  }
  if (error || !data) {
    return <div className="text-destructive" data-testid="colors-error">Failed to load brand data.</div>;
  }

  const { colors } = data;

  return (
    <div className="space-y-8" data-testid="color-palette-section">
      <div>
        <h1 className="text-4xl md:text-5xl font-display font-normal text-jackstone-blue mb-4">
          Color Palette
        </h1>
        <p className="text-base text-body leading-relaxed">
          Our color system reflects industrial precision and professional confidence.
          Each color has been carefully selected for accessibility and brand impact.
        </p>
      </div>

      <ColorSection
        title="Primary Brand Colors"
        colors={colors.primary}
        gridCols="grid-cols-2 md:grid-cols-3 lg:grid-cols-6"
      />

      <ColorSection
        title="Neutral & Text Colors"
        colors={colors.neutral}
        gridCols="grid-cols-3"
      />

      <ColorSection
        title="Background Colors"
        colors={colors.background}
        gridCols="grid-cols-2 md:grid-cols-3 lg:grid-cols-5"
      />

      <ColorSection
        title="Accent & Graph Colors"
        colors={colors.accent}
        gridCols="grid-cols-2 md:grid-cols-3 lg:grid-cols-6"
      />

      <Card className="p-6">
        <h3 className="text-xl font-heading font-semibold text-jackstone-blue mb-4">
          Color Usage Guidelines
        </h3>
        <div className="space-y-4">
          <div>
            <Badge tone="jackstone-blue" fill="soft" className="mb-2">Primary Usage</Badge>
            <p className="text-sm text-subtle">{colors.usage.primary}</p>
          </div>
          <div>
            <Badge tone="grey" fill="soft" className="mb-2">Secondary Usage</Badge>
            <p className="text-sm text-subtle">{colors.usage.secondary}</p>
          </div>
          <div>
            <Badge tone="teal" fill="soft" className="mb-2">Accent Usage</Badge>
            <p className="text-sm text-subtle">{colors.usage.accent}</p>
          </div>
        </div>
      </Card>

      <Card className="p-6 bg-surface">
        <h3 className="text-xl font-heading font-semibold text-jackstone-blue mb-4">
          Accessibility Considerations
        </h3>
        <p className="text-body mb-4">
          All color combinations have been tested for WCAG AA compliance:
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          {colors.accessibility.map((a) => (
            <div key={a.pairing} className="bg-background p-4 rounded-lg border">
              <p className="text-body font-medium text-sm">{a.pairing}</p>
              <p className="text-subtle text-xs">
                Contrast ratio: {a.ratio} — {a.level}
              </p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
